/**
 * The source language.
 *
 * Keys are grouped by the surface they appear on, and named for what the
 * string says rather than where it sits, so moving a line between pages does
 * not orphan a key. Anything a customer never sees — admin, error codes,
 * provider jargon — is deliberately absent: staff work in one language and
 * translating a queue nobody outside the company reads is cost with no
 * reader.
 */
export default {
  nav: {
    home: 'Home',
    services: 'Services',
    faq: 'FAQ',
    help: 'Help',
    signIn: 'Sign in',
    getStarted: 'Get started',
    openPanel: 'Open panel',
    language: 'Language',
    currency: 'Currency',
  },

  common: {
    perThousand: 'per 1,000',
    from: 'From {amount} per 1,000',
    noSubscription: 'No subscription',
    neverPassword: 'Never asked for a password',
    cancel: 'Cancel',
    done: 'Done',
    total: 'Total',
    rate: 'Rate',
    range: 'Range',
    starts: 'Starts',
    refill: 'Refill',
    quantity: 'Quantity',
    lowest: 'lowest',
    services: 'services',
    platforms: 'platforms',
    shownIn: "Shown in {display} at today's rate · charged in {platform}",
    chargedIn: 'Charged in {code}',
    cancelWhilePending: 'while the order is still pending',
    notOffered: 'not offered',
    chargedAs: 'charged as {amount}',
  },

  home: {
    refresh: {
      titleA: 'Social media services.',
      titleB: 'Clear prices. Trackable orders.',
      lede: 'Browse followers, likes and views for TikTok, Instagram and YouTube. Review prices, delivery estimates and refill terms before you order.',
      eyebrowIdle: 'Explore our services',
      demo: 'Order tracking demo',
      demoNote: 'Example orders · animation runs at 60× speed',
      servicesKey: 'Explore the catalogue',
      servicesTitle: 'Find a service that fits.',
      servicesBody:
        'Compare current rates and service terms before creating an account. Start times are estimates, not completion guarantees.',
      perThousand: 'per 1,000',
      minimum: 'Minimum quantity',
      start: 'Estimated start',
      notListed: 'Not listed',
      refill: 'Refill coverage',
      days: '{count} days',
      seeTerms: 'See service terms',
      details: 'View service details',
      loading: 'Loading current services…',
      empty: 'No services are available to preview right now. Check the catalogue for availability.',
      currencyNote:
        'Converted prices are estimates. Review the charge currency and final total before paying.',
      faqKey: 'Before you order',
      faqTitle: 'Know what to expect.',
      moreAnswers: 'More questions answered',
      closingTitle: 'Find your first service.',
      closingBody: 'Compare prices and terms. Choose your quantity when you are ready.',
      steps: {
        chooseTitle: 'Choose a service',
        chooseBody: 'Browse by platform and compare the price, minimum quantity and refill coverage.',
        reviewTitle: 'Review the details',
        reviewBody:
          'Enter your public link and quantity. Check the total, delivery estimate and service conditions before confirming.',
        trackTitle: 'Track your order',
        trackBody:
          'Follow the reported status in your account. Contact support if an order stalls or the result needs attention.',
      },
      faq: {
        deliveryTitle: 'How long does delivery take?',
        deliveryBody:
          'Timing varies by service and quantity. A start estimate describes when delivery may begin, not when it will finish. Delays can occur; contact support if your order exceeds its stated estimate.',
        dropsTitle: 'Can followers or other counts drop?',
        dropsBody:
          'Yes. Counts can fall after delivery. Only services marked with refill coverage are eligible for replacement, within their listed window and conditions. No-refill services do not include replacement.',
        refundsTitle: 'Where do refunds go?',
        refundsBody:
          'When an order is confirmed canceled or partial, the eligible undelivered amount is returned to your PresenceRise wallet. This is account credit, not a refund to your card. An in-progress order is not automatically refundable just because it is delayed.',
        passwordTitle: 'Do I need to share my social media password?',
        passwordBody:
          'No. Orders use your public profile or post link. Never include your password or login codes in an order or support message.',
        paymentsTitle: 'Which payment methods can I use?',
        paymentsBody:
          'Available methods are shown at checkout or when adding funds. They can vary by country and currency. Check the available options and any fees before paying.',
      },
    },
    eyebrow: '{count} services live across {platforms} platforms',
    eyebrowIdle: 'Now accepting orders',
    titleA: 'Growth you can',
    titleB: 'watch happen.',
    lede: 'Followers, likes, views and subscribers, priced per thousand and delivered through a pipeline you can see. Every order is tracked live, measured afterwards, and refunded or refilled on its own when it falls short.',
    ctaStart: 'Get started free',
    ctaBrowse: 'Browse services',
    ctaOrder: 'Place an order',
    delivering: '{count} delivering',
    illustration: 'Illustration, running at 60× speed',
    figures: {
      services: 'services',
      platforms: 'platforms',
      from: 'from, per 1,000',
      refill: 'with a refill window',
      remeasured: 'days each order is re-measured',
      tickets: 'tickets needed for a refund',
    },
    life: {
      key: 'The life of an order',
      title: 'Six things happen to every order. None of them need you.',
      pricedTitle: 'Priced on the server',
      pricedBody:
        'Your quantity is priced by the same code the API uses. The figure you approve is the figure charged.',
      chargedTitle: 'Charged once',
      chargedBody:
        'Your wallet is debited at the moment the order is created, and never again for that order.',
      sentTitle: 'Submitted and tracked',
      sentBody:
        'Routed to the provider holding delivery best for that service, then polled until it finishes.',
      deliveringTitle: 'Delivering',
      deliveringBody:
        'Watched on the Climb: where you started, where you are, and where you will be at the current rate.',
      measuredTitle: 'Measured at 1, 7 and 30 days',
      measuredBody:
        'The delivered count is re-read and compared with completion. The percentage that stayed is published.',
      settledTitle: 'Refilled or refunded, on its own',
      settledBody:
        'A drop inside the refill window files a refill. A short delivery refunds the difference to your wallet.',
    },
    kept: {
      key: 'Why we are not the cheapest',
      title: 'The cheapest follower is the one that stays.',
      body: 'A lower rate that loses a third of what it delivered costs more per follower you keep than a dearer one that holds. Every order is routed on the price per surviving unit, not the sticker price, and the retention figure behind that decision is printed on the service for you to check.',
      link: 'The full answer, in the FAQ',
      sticker: 'Sticker rate',
      held: 'Held at 30 days',
      effective: 'Per 1,000 kept',
      cheaper: 'The cheaper listing',
      ours: 'The one we route to',
      foot: 'Illustrative figures. The real ones are on each service, with the sample size.',
    },
    steps: {
      fundsTitle: 'Add funds',
      fundsBody: 'A card, or GCash, Maya and GrabPay in the Philippines. No subscription.',
      pickTitle: 'Pick a service',
      pickBody: 'Choose the outcome, paste a public link, see the exact total before you confirm.',
      watchTitle: 'Watch it climb',
      watchBody: 'Delivery is tracked live. Short delivery refunds itself; a drop refills itself.',
    },
    cta: {
      title: 'Ready when you are.',
      body: 'No subscription, no minimum spend, no password ever asked for.',
      account: 'Create your account',
    },
  },

  services: {
    key: 'Services',
    titleA: 'Every service,',
    titleB: 'every price.',
    lede: 'One calibrated price axis per outcome, so the spread of the marks is the market. All rates are per 1,000 units, with nothing added at checkout. Click a row to price a real quantity.',
    quality: 'Quality',
    region: 'Region',
    anyQuality: 'Any quality',
    anyRegion: 'Any region',
    allPlatforms: 'All platforms',
    search: 'Search services',
    refillOnly: 'Refill only',
    sort: 'Sort',
    sortPrice: 'Price',
    sortRetention: 'Retention',
    sortName: 'Name',
    empty: 'No services match',
    emptyBody: 'Try a different search or clear the filters.',
    showEverything: 'Show everything',
    priceIt: 'Price it',
    priceEmpty:
      'Click any row and its price for a real quantity appears here — the same figure the order form will charge.',
    order: 'Order this',
    orderGuest: 'Create an account to order',
    includes: 'What the price includes',
    terms: {
      perThousandTitle: 'Rates are per 1,000 units',
      perThousandBody:
        'Every service is quoted the same way, so any two are directly comparable. A rate of $1.23 buys 1,000; 500 costs half that.',
      totalTitle: 'The total is calculated before you confirm',
      totalBody:
        'Your quantity is priced on our server and shown in full. The figure you approve is the figure charged — nothing is added at checkout.',
      balanceTitle: 'You top up a balance, not a subscription',
      balanceBody:
        'Add funds when you want them and spend from that balance. Nothing recurring, no monthly minimum.',
      refundTitle: 'Short orders are refunded automatically',
      refundBody:
        'If an order underdelivers, the undelivered portion returns to your balance on its own. You do not have to notice it or open a ticket.',
      refillTitle: 'Many services include a refill window',
      refillBody:
        'Where a service offers one its length is shown on the row, and a refill is filed for you when a retention check finds a drop.',
    },
  },

  auth: {
    signInKey: 'Sign in',
    signInTitle: 'Welcome back.',
    signInLede: 'Your orders, balance and anything still climbing are where you left them.',
    identifier: 'Username or email',
    password: 'Password',
    passwordPlaceholder: 'Your password',
    passkey: 'Sign in with a passkey',
    or: 'or',
    forgot: 'Forgot?',
    remember: 'Keep me signed in for 30 days',
    newHere: 'New here?',
    createAccount: 'Create an account',
    show: 'Show',
    hide: 'Hide',

    registerKey: 'Create account',
    registerTitle: 'Order in under a minute.',
    registerLede: 'A username and a password is all it takes.',
    registerCarries: 'The service you chose is waiting on the other side.',
    registerClosed: 'Registration is closed at the moment. Please check back soon.',
    username: 'Username',
    usernameHint: 'Letters, numbers, dots, dashes and underscores.',
    newPassword: 'Password',
    newPasswordPlaceholder: 'At least 10 characters',
    noEmailTitle: 'No email needed to start',
    noEmailBody:
      'You can add one at checkout. Until you do, we cannot recover your account, so an account with no email is removed after 5 days. Adding one keeps it — and anything you have paid for is never removed automatically.',
    agreeA: 'I agree to the',
    terms: 'Terms of Service',
    agreeB: 'and',
    privacy: 'Privacy Policy',
    haveAccount: 'Already have an account?',

    caseKey: 'How ordering here works',
    caseTitle: 'Growth you can watch happen.',
    caseNoEmailTitle: 'No email needed to start',
    caseNoEmailBody:
      'A username and a password. You can add an email at checkout, where it is a receipt rather than a toll gate.',
    casePricedTitle: 'Priced before you confirm',
    casePricedBody:
      'Every total is calculated on our servers and shown in full. The figure you approve is the figure charged.',
    caseSettledTitle: 'Refunded or refilled on its own',
    caseSettledBody:
      'Short delivery returns to your wallet without a ticket. A drop inside the refill window files the refill for you.',
    backToSite: 'Back to site',
  },

  upsell: {
    title: 'Make it look organic',
    sameLink: 'Everything here is delivered to the same link as the order above.',
    why: {
      likesForPlays: 'Plays that bring likes with them are how a track looks like it is catching on.',
      likesForViews:
        'A video with views and no likes reads as bought. Likes at a few percent of the views look like people watched it.',
      viewsForLikes:
        'Likes without views is the odd one: nobody likes a video they have not watched, and on a new video the ratio shows.',
      subsForViews:
        'A few of every thousand viewers follow. Views that bring nobody along look like a number, not an audience.',
      likesForFollowers:
        'New followers who like nothing are noticeable. A few likes on recent posts make the growth read as people.',
      viewsForSubs:
        'Subscribers arrive from videos that were watched. Views on the latest video make the new subscribers make sense.',
      likesForSubs: 'Someone who subscribes usually liked something first.',
      followersForPlays: 'Plays from listeners who then follow are how a track looks like it is catching on.',
      likesForComments: 'Comments outnumbering likes is a tell. Most people who comment also like.',
      likesForShares: 'Shares without likes rarely happen; the likes make the shares believable.',
      viewsForReactions:
        'Reactions on a post that few people saw stand out. Views first, then the reactions read as real.',
    },
  },

  guest: {
    orderWithout: 'Order without an account',
    orderTitle: 'Order as a guest',
    orderLede:
      'Pay, and the order is placed. The receipt email carries a one-click link to create your account with this order already in it.',
    email: 'Email for the receipt',
    emailHint: 'The order details and the link to create your account go here. Nothing else is sent.',
    linkLabel: 'Link or username',
    linkHint: 'A link to a public profile or post. Private accounts cannot be delivered to.',
    payWith: 'Pay with',
    noMethods: 'Payments are being set up. Sign in to order from your balance.',
    pay: 'Pay {amount}',
    methods: {
      xenditPhLabel: 'GCash, Maya & bank transfer',
      xenditPh: 'Plus GrabPay, ShopeePay and over-the-counter at 7-Eleven',
      xenditId: 'OVO, DANA, bank transfer and retail outlets',
      local: 'Charged in {currency} · no conversion',
    },
    feeNote: 'Includes {fee} {method} processing fee, so the order is paid in full.',
    minimumNote: 'The minimum for this method is {amount}; the difference stays as balance on your account.',
    basket: 'Basket',
    haveAccount: 'Have an account?',
    status: {
      eyebrow: 'Your order',
      waitingTitle: 'Confirming your payment.',
      waitingLede:
        'The payment processor is telling us it went through. This usually takes a few seconds; the page updates itself.',
      placedTitle: 'Your order is placed.',
      placedLede:
        'Delivery starts on its own. The receipt is on its way to your email, with the link that makes this your account.',
      failedTitle: 'The payment did not complete.',
      failedLede: 'Nothing was charged and nothing was placed. You can start again whenever you like.',
      canceledLede: 'The payment was cancelled before it completed. Nothing was charged.',
      reference: 'Reference',
      paid: 'Paid',
      email: 'Receipt to',
      polling: 'Waiting for the processor…',
      failedItems:
        '{count} item(s) could not be placed. The money is held as balance on your account; the email explains.',
      nextKey: 'Next',
      nextBody:
        'Open the email and click "Create my account". You choose a username and a password, and these orders are on screen — with live delivery, refills and refunds handled for you. The link is good for 30 days.',
      help: 'Help centre',
      tryAgain: 'Try again',
      notFound: 'We could not find that payment.',
    },
    claim: {
      key: 'Create your account',
      loading: 'One moment…',
      title: 'Choose a username and a password.',
      lede: 'That is all. Your {count} paid order(s) are already on this account.',
      emailConfirmed: '{email} is confirmed — you clicked the link we sent to it.',
      submit: 'Create account and see my orders',
      invalidTitle: 'This link has expired.',
      invalidLede:
        'Claim links last 30 days. If you already created the account, sign in; otherwise ask the help centre and quote your order number.',
    },
  },

  footer: {
    tagline: 'Growth you can watch happen.',
    blurb:
      'Browse social media services with clear prices and order tracking. Delivery estimates, refill coverage and refund eligibility depend on the service.',
    startKey: 'Start in under a minute',
    createAccount: 'Create account',
    getHelp: 'Get help',
    startNote:
      'A username and a password is enough. Support runs through tickets, already attached to your account and orders.',
    product: 'Product',
    help: 'Help',
    legal: 'Legal',
    platforms: 'Platforms',
    pricing: 'Pricing',
    signIn: 'Sign in',
    helpCentre: 'Help centre',
    terms: 'Terms of service',
    privacy: 'Privacy policy',
    rights: '© {year} {site}. All rights reserved.',
    mono: 'Rates per 1,000 · review your total before paying',
    notAffiliated: 'Not affiliated with any social media platform.',
    top: 'Top',
  },

  /* The public FAQ. Answers are numbered paragraphs (a1, a2, a3) because
   * the view renders each as its own <p>; a question with two paragraphs
   * simply has no a3. */
  faq: {
    eyebrow: 'Help centre',
    titleA: 'The things worth asking',
    titleEm: 'before',
    titleB: 'you pay',
    lede: 'Including the ones with awkward answers. If it is not here, ask — the list grows from what people actually ask.',
    searchPlaceholder: 'Search {count} questions',
    searchLabel: 'Search questions',
    clearSearch: 'Clear search',
    try: 'Try',
    /* Words people actually type, comma-separated. Each one is a search, so
     * they must be words that appear in the questions or answers above. */
    suggestions: 'refund, password, GCash, cancel, drop',
    topics: 'Topics',
    ofTotal: 'of {total}',
    inTopic: 'in {topic}',
    matching: 'matching “{query}”',
    showAll: 'Show everything',
    emptyTitle: 'Nothing matches “{query}”.',
    emptyText: 'Try a different word, or ask directly — an unanswered question is how this list grows.',
    previous: 'Previous',
    next: 'Next',
    stillUnsure: 'Still unsure? Open a ticket',
    contactKey: 'Still stuck?',
    contactTitle: 'Ask the people who run it',
    contactText:
      'Support runs through tickets in the panel rather than an inbox. It reaches the same people, and it arrives already attached to your account and your orders — so nobody has to ask you for an order number.',
    contactNote: 'No account yet? Making one takes a username and a password, and about thirty seconds.',
    openTicket: 'Open a ticket',
    createAccount: 'Create an account',
    groups: {
      quality: { label: 'Quality & drop', blurb: 'Whether they stay, and what we do when they do not.' },
      orders: { label: 'Orders', blurb: 'Speed, links, partial delivery and cancelling.' },
      money: { label: 'Payments', blurb: 'Paying, regional prices and your wallet.' },
      account: { label: 'Account & safety', blurb: 'Passwords, platform risk, signing up, your data.' },
    },
    q: {
      retention: {
        q: 'Do the followers stay, or do they disappear?',
        a1: 'Some drop. Anyone who tells you otherwise is selling you something — accounts get deleted, people unfollow, and platforms remove inauthentic engagement in waves.',
        a2: 'What is different here is that we measure it. Every completed order is re-checked at 1, 7 and 30 days and the delivered count compared against what it was at completion. Where we have enough data, the percentage that stayed is printed on the order form next to the price, along with how many orders it was measured across.',
        a3: 'We publish that number even when it is bad. A service holding 78% is shown as 78%.',
      },
      refill: {
        q: 'What happens if my order drops?',
        a1: 'On a service sold with a refill guarantee, we file the refill ourselves. You do not need to notice, and you do not need to ask — the same measurement that produces the retention figure triggers the replacement.',
        a2: 'This is the single most common complaint in this industry: a refill guarantee sold and then quietly ignored when someone tries to use it. Removing the asking is the point.',
      },
      qualityVsPrice: {
        q: 'Why are you not the cheapest?',
        a1: 'Because the cheapest is usually not the cheapest. A thousand followers at a lower price that loses a third of them costs more per follower you actually keep than a dearer one that holds.',
        a2: 'We route every order on price per surviving follower rather than on the sticker price, and the same logic sets what we charge. Occasionally that does make us the cheapest; when it does not, the difference is buying delivery that stays.',
      },
      speed: {
        q: 'How quickly does an order start?',
        a1: 'Most services begin within minutes. Each one shows its own typical start time and speed on the order form, taken from what that service has actually been doing rather than from a promise.',
        a2: 'An order that has not moved is visible in your order list with its live status, and you are emailed when it finishes.',
      },
      partial: {
        q: 'What if an order is only partly delivered?',
        a1: 'You are refunded the undelivered part automatically, at the rate you paid, and the money is back in your wallet without you asking. A fully failed order is refunded in full the same way.',
        a2: 'Refunds are computed from the order and written to the ledger by the system, not by a person deciding whether to honour them.',
      },
      cancel: {
        q: 'Can I cancel an order?',
        a1: 'On services that support cancellation, yes — the option appears on the order itself. Many services cannot be cancelled once delivery has begun, and the order form says which before you buy rather than after.',
      },
      link: {
        q: 'What link should I give you?',
        a1: 'Whatever the service asks for: a profile URL for followers, a post URL for likes or views, sometimes just a username. The field tells you which and rejects the wrong shape before you pay rather than failing afterwards.',
        a2: 'Your account must be public while an order runs. A private account cannot receive delivery, and that is not something a refill can fix.',
      },
      payment: {
        q: 'How do I pay?',
        a1: 'Card, and local payment methods where we support them — in the Philippines that means GCash, Maya and GrabPay.',
        a2: 'Money goes into a wallet balance first, and orders draw from it. That means an order never waits on a payment clearing, and refunds land instantly rather than being sent back through the card network.',
      },
      regional: {
        q: 'Why are prices lower for some countries?',
        a1: 'Because a price that is reasonable in London is not reasonable in Manila. Customers paying from a supported country get a local price list.',
        a2: 'It is earned through the payment method rather than by where you appear to be browsing from — a local e-wallet or a card issued in that country. That is fairer than location detection, which is trivial to fake and would wrongly shut out people working abroad who buy for family at home.',
      },
      refundWallet: {
        q: 'Can I withdraw my wallet balance?',
        a1: 'Wallet funds are for orders and are not withdrawable. Deposit what you plan to spend rather than parking a balance.',
        a2: 'Anything refunded from a failed or partial order returns to the wallet and can be spent on anything else.',
      },
      password: {
        q: 'Do you need my password?',
        a1: 'No. Never. We ask for a public link or a username and nothing else.',
        a2: 'Any service anywhere asking for your social media password is either compromising your account or about to. There is no legitimate reason for it.',
      },
      safe: {
        q: 'Is this safe for my account?',
        a1: 'We never touch your login, and delivery happens from outside your account. The honest caveat is that buying engagement is against the terms of service of every major platform, and enforcement is theirs, not ours.',
        a2: 'Practically: keep orders proportionate to your account size, avoid ordering the same thing repeatedly in a short window, and treat anything that looks sudden as a risk. A slow build looks like a slow build.',
      },
      signup: {
        q: 'Why can I sign up without an email?',
        a1: 'So you can order in under a minute. A username and a password is enough to start.',
        a2: 'The trade is real and we say it up front: with no email on file we cannot recover your account or send you a receipt, so an account without one is removed after 5 days. Adding an email at any point keeps it. An account holding a balance, orders or payments is never removed automatically — it is flagged for a person instead.',
      },
      privacy: {
        q: 'What do you do with my data?',
        a1: 'We keep what running your orders requires and nothing beyond it. We do not sell it, and we do not post anything anywhere.',
        a2: 'The full detail is in the privacy policy, which is written to be read rather than to be survived.',
      },
    },
  },

  /* The help centre: find an order without signing in, or open a ticket. */
  help: {
    key: 'Help centre',
    title: 'Ask the people who run it.',
    lede: 'There is no inbox here. Everything becomes a ticket in the one queue our staff work from, and you never need an email address to be helped, or to read the answer.',
    points: {
      findTitle: 'Locked out? Find your order',
      findBody:
        'The order number, the username on the account, and the last four characters of a payment reference prove it is yours. Then you see exactly what is happening to it.',
      autoTitle: 'Most answers need no person',
      autoBody:
        'Short delivery refunds itself, a drop refills itself, and the order page says which happened. When it does need a person, hand it over from the same screen.',
      repliesTitle: 'Replies live on the ticket',
      repliesBody:
        'Signed in, under Support. Signed out, on a page that a number and a key open. Nothing is emailed, and we never ask for a social media password.',
    },
    /* {link} is the "Sign in" / "Support" link. */
    asideGuest:
      'Have an account and remember the password? {link} and tickets are kept under Support for you.',
    asideGuestLink: 'Sign in',
    asideUser: 'Your tickets are under {link}, with your orders beside them.',
    asideUserLink: 'Support',
    laneOrder: 'Find my order',
    laneTicket: 'Open a ticket',

    findFailed: 'We could not match those details to an order.',
    refreshFailed: 'Could not refresh the order',
    orderNumber: 'Order number',
    orderNumberPlaceholder: 'e.g. 1042',
    orderNumberHint: 'Shown on the order page and in the confirmation.',
    username: 'Username on the account',
    usernamePlaceholder: 'yourname',
    last4: 'Last four characters of a payment reference',
    last4Placeholder: 'e.g. 9QX2',
    last4Hint:
      'Any completed deposit on the account. References look like PAY-7F3K9QX2 on the wallet page and receipts.',
    findOrder: 'Find the order',
    findFoot:
      'No payment reference to hand? Open a ticket instead and give the order number; a person will verify it with you.',

    status: {
      PENDING: 'Queued',
      QUEUED: 'Queued',
      SUBMITTING: 'Submitting',
      PROCESSING: 'Processing',
      IN_PROGRESS: 'Delivering',
      PARTIAL: 'Partial',
      COMPLETED: 'Completed',
      CANCELED: 'Cancelled',
      FAILED: 'Failed',
      REFUNDED: 'Refunded',
    },
    orderKey: 'Order #{number}',
    orderMeta: '{platform} · {quantity} ordered · {charge} · placed {date}',
    done: 'Done',
    providerNote: 'Provider note: {note}',
    refundedToWallet: 'Refunded to the wallet',
    checkAfter: 'Check after {n} day | Check after {n} days',
    stillThere: '{held}% still there',
    due: 'due {date}',
    refillAt: 'Refill {date}',
    ticketNumber: 'Ticket #{number}',

    escalatedTitle: 'Ticket #{number} is with a person.',
    escalatedBody:
      'It is filed under this order and under the account. This link opens it while you are locked out; it is the only copy of the key.',
    copy: 'Copy',
    copied: 'Copied',
    copyLink: 'Copy the link',
    openTheTicket: 'Open the ticket',
    needPersonTitle: 'Still need a person?',
    needPersonBody: 'Hand this order to support. They see everything above, plus the provider side.',
    escalate: 'Escalate to a person',
    escalateLabel: 'What should they know?',
    escalatePlaceholder: 'What you expected, what you see, and anything the numbers above do not explain.',
    reachYou: 'A way to reach you',
    reachYouEscPlaceholder: 'Optional: a handle. Replies appear on the ticket either way.',
    escalateFailed: 'Could not open the ticket',

    emailAttached: 'Email attached.',
    noEmailTitle: 'This account has no email address.',
    noEmailBody:
      'Attach one and the password can be reset the normal way. It is only used for that and for receipts.',
    attachEmail: 'Attach an email',
    emailLabel: 'Email for this account',
    emailPlaceholder: "you{'@'}example.com",
    attachSend: 'Attach and send verification',
    attachFailed: 'Could not attach that email',
    viewStays: 'This view stays open for 30 minutes.',
    refreshOrder: 'Refresh the order',

    yourTickets: 'Your open tickets',
    ticketOpened: 'Ticket opened',
    /* {number} is the ticket number, rendered in colour. */
    queuedTitle: 'You are {number} in the queue.',
    queuedToast: '#{number} is in the queue.',
    sentLede:
      'Staff have been told. This link opens your ticket, shows every reply, and lets you answer. It is the only copy of your key, so keep it somewhere safe.',
    sendAnother: 'Send another',
    sentNote:
      'This browser also remembers the key, so returning to the ticket page here will work without the link.',
    yourName: 'Your name',
    yourNamePlaceholder: 'What should we call you?',
    reachYouPlaceholder: 'Optional: a handle, or your username here',
    reachYouHint: 'Only if you want one. Replies appear on the ticket either way.',
    about: 'This is about',
    topic: 'Topic',
    topics: {
      ORDER: 'An order',
      PAYMENT: 'A payment',
      ACCOUNT: 'My account',
      REFILL: 'A refill',
      OTHER: 'Something else',
    },
    subject: 'Subject',
    subjectPlaceholder: 'One line on what this is about',
    message: 'Message',
    messagePlaceholder:
      'What happened, what you expected, and anything that helps us find it: an order number, a link, a time.',
    messageHint: 'Order numbers and links help. Passwords never — we will not ask.',
    submitGuest: 'Open a ticket',
    submitUser: 'Open a ticket under my account',
    sendFailed: 'Could not send your message',
    /* {link} is the "FAQ" link. */
    faqFoot:
      'Looking for an answer first? The {link} covers the questions people actually ask, awkward answers included.',
    faqFootLink: 'FAQ',
  },

  /* A guest ticket, opened with its number and key. */
  ticket: {
    number: 'Ticket #{number}',
    opening: 'Opening…',
    needsKey: 'This ticket needs its key.',
    wrongKey: 'That key does not open this ticket.',
    needsKeyBody:
      'The link you were given when you opened the ticket carries it. Paste the key from that link below.',
    mismatch: 'That ticket number and key do not match.',
    keyLabel: 'Key',
    keyPlaceholder: 'Paste the key from your link',
    openTicket: 'Open ticket',
    /* {link} is "Open a new ticket"; {number} the ticket number. */
    lostKey: 'Lost the key? It cannot be recovered. {link} and mention #{number} so we can join the two.',
    lostKeyLink: 'Open a new ticket',
    status: {
      OPEN: 'Open',
      PENDING_SUPPORT: 'Waiting on support',
      PENDING_CUSTOMER: 'Waiting on you',
      RESOLVED: 'Resolved',
      CLOSED: 'Closed',
    },
    opened: 'Opened {date}',
    with: 'with {name}',
    staff: '{name} · Support',
    you: 'You',
    reply: 'Reply',
    replyPlaceholder: 'Add anything that helps, or answer a question from support.',
    repliesHere: 'Replies appear here; nothing is emailed.',
    sendReply: 'Send reply',
    sendFailed: 'Could not send your reply',
    /* {link} is "open a new one"; {number} the ticket number. */
    closed: 'This ticket is closed. If something is still wrong, {link} and mention #{number}.',
    closedLink: 'open a new one',
  },

  /* The chrome around a legal document: the rail, the aside label, the
   * footer. The documents themselves are separate files per language. */
  legal: {
    updated: 'Updated {date}',
    sections: '{n} section | {n} sections',
    minRead: '{n} min read',
    contents: 'Contents',
    plainWords: 'In plain words',
    keepReading: 'Keep reading',
    backToTop: 'Back to top',
  },
}
